import { useNavigationStore } from "@/store/navigationStore";
import { useAppStore } from "@/store/appStore";
import { CHAT_TYPE } from "@/types/navigation-mode";
import { useEffect, useRef, useState } from "react";
import BottomSheet from "../bottomsheet/bottom-sheet";
import Text from "./../text/text";
import CloseButton from "./close-button";
import Image from "./../image";
import { useTranslation } from "react-i18next";
import { useCrimeSceneStore } from "src/store/crimeSceneStore";

import icon_victimM from "src/assets/crime/icon_victim_m.png";
import icon_victimF from "src/assets/crime/icon_victim_f.png";
import icon_prisoner from "src/assets/crime/icon_prisoner.png";
import icon_police from "src/assets/crime/icon_police.png";
import icon_citizenF from "src/assets/crime/icon_citizen_f.png";
import icon_citizenM from "src/assets/crime/icon_citizen_m.png";
import icon_security from "src/assets/crime/icon_security.png";
import icon_paramedic from "src/assets/crime/icon_paramedic.png";
import ImageButton from "./image-button";
import useMdBreakpoint from "@/hooks/useMdBreakpoint";
import { useExplorationStore } from "@/store/explorationStore";

const defaultView = {
  id: "observationCamera",
  type: "third_pov",
  name: "3rd person view",
  color: "",
  img: "/assets/icon_pov_third.png",
};

const CharacterTypes = {
  attacker: "attacker",
  police: "police",
  victimF: "victimF",
  victimM: "victimM",
  citizenF: "citizenF",
  citizenM: "citizenM",
  paramedics: "paramedics",
  guard: "guard",
};

const characters = {
  police: {
    type: CharacterTypes.police,
    color: "#20C7FF",
    img: icon_police,
  },
  attacker: {
    type: CharacterTypes.attacker,
    color: "#FF0EE7",
    img: icon_prisoner,
  },
  victimF: {
    type: CharacterTypes.victimF,
    color: "#FFD400",
    img: icon_victimF,
  },
  victimM: {
    type: CharacterTypes.victimM,
    color: "#20C7FF",
    img: icon_victimM,
  },
  citizenF: {
    type: CharacterTypes.citizenF,
    color: "#18FFA6",
    img: icon_citizenF,
  },
  citizenM: {
    type: CharacterTypes.citizenM,
    color: "#18FFA6",
    img: icon_citizenM,
  },
  paramedics: {
    type: CharacterTypes.paramedics,
    color: "#FF6200",
    img: icon_paramedic,
  },
  guard: {
    type: CharacterTypes.guard,
    color: "#FF6200",
    img: icon_security,
  },
};

const characterViews = [
  {
    id: "prisoner",
    name: "prisoner",
    type: CharacterTypes.attacker,
  },
  {
    id: "police1",
    name: "police1",
    type: CharacterTypes.police,
    index: 1,
  },
  {
    id: "police2",
    name: "police2",
    type: CharacterTypes.police,
    index: 2,
  },
  {
    id: "police3",
    name: "police3",
    type: CharacterTypes.police,
    index: 3,
  },
  {
    id: "police4",
    name: "police4",
    type: CharacterTypes.police,
    index: 4,
  },
  {
    id: "victimM",
    name: "victimM",
    type: CharacterTypes.victimM,
    index: 1,
  },
  {
    id: "victimF",
    name: "victimF",
    type: CharacterTypes.victimF,
    index: 2,
  },
  {
    id: "citizenF",
    name: "citizenF",
    type: CharacterTypes.citizenF,
    index: 1,
  },
  {
    id: "citizenM",
    name: "citizenM",
    type: CharacterTypes.citizenM,
    index: 2,
  },
  {
    id: "paramedic1",
    name: "paramedic1",
    type: CharacterTypes.paramedics,
    index: 1,
  },
  {
    id: "paramedic2",
    name: "paramedic2",
    type: CharacterTypes.paramedics,
    index: 2,
  },
  {
    id: "security",
    name: "security",
    type: CharacterTypes.guard,
  },
];

const views = [defaultView, ...characterViews];

const PovButton = () => {
  const switchCameraMode = useNavigationStore(
    (state) => state.switchCameraMode
  );
  const switchExplorationCameraMode = useExplorationStore(
    (state) => state.switchExplorationCameraMode
  );
  const chatType = useAppStore((state) => state.chatType);
  const setFocusingCharacter = useCrimeSceneStore(
    (state) => state.setFocusingCharacter
  );
  const cameraMode = useNavigationStore((state) => state.cameraMode);
  const explorationCameraMode = useExplorationStore(
    (state) => state.cameraMode
  );
  const simulationData = useCrimeSceneStore((state) => state.simulationData);
  const [openBottomSheet, setOpenBottomSheet] = useState(false);
  const [isFirstPov, setFirstPov] = useState(true);
  const [selectedView, setSelectedView] = useState(defaultView.img);
  const [selectedPov, setSelectedPov] = useState(defaultView.type);
  const { t } = useTranslation();
  const isMd = useMdBreakpoint();
  const sceneIndex = useCrimeSceneStore((state) => state.sceneIndex);
  const latestScene = useRef(0);

  useEffect(() => {
    if (latestScene.current !== sceneIndex) {
      latestScene.current = sceneIndex;
      setSelectedView(defaultView.img);
      setSelectedPov(defaultView.type);
    }
  }, [sceneIndex]);

  useEffect(() => {
    if (chatType === CHAT_TYPE.NAVIGATION) {
      setFirstPov(cameraMode === 0);
    } else if (chatType === CHAT_TYPE.EXPLORATION) {
      setFirstPov(explorationCameraMode === 0);
    }
  }, [cameraMode, explorationCameraMode]);

  const onClick = () => {
    if (chatType === CHAT_TYPE.NAVIGATION) {
      switchCameraMode();
      setFirstPov(!isFirstPov);
    } else if (chatType === CHAT_TYPE.SIMULATION) {
      setOpenBottomSheet(true);
    } else if (chatType === CHAT_TYPE.EXPLORATION) {
      switchExplorationCameraMode();
      setFirstPov(!isFirstPov);
    }
  };

  const povIcon = () => {
    console.log("chatType", chatType);
    if (
      chatType === CHAT_TYPE.NAVIGATION ||
      chatType === CHAT_TYPE.EXPLORATION
    ) {
      return isFirstPov
        ? "/assets/icon_pov_first.png"
        : "/assets/icon_pov_third.png";
    } else if (chatType === CHAT_TYPE.SIMULATION) {
      return selectedView;
    }
    return "/assets/icon_pov_third.png";
  };

  const povIconSize = () => {
    if (
      chatType === CHAT_TYPE.NAVIGATION ||
      chatType === CHAT_TYPE.EXPLORATION
    ) {
      return 24;
    } else if (chatType === CHAT_TYPE.SIMULATION) {
      return selectedView === defaultView.img ? 24 : 48;
    }
    return 48;
  };

  const onSelectCharacterPov = (view: any, img: string) => {
    console.log("!!!! " + img);
    setSelectedView(img);
    setSelectedPov(view.name);
    setOpenBottomSheet(false);
    console.log(`set pov to ${view.name}`);
    setFocusingCharacter(view.id);
  };

  const getColorAndImage = (type: string) => {
    const character = Object.values(characters).find((c) => c.type === type);
    if (!character) return defaultView;
    return { img: character.img, color: character.color };
  };

  const PovView = () => {
    return (
      <div className="flex flex-col">
        <div className="flex w-full justify-between items-center mb-[20px]">
          <Text text="View" size={18} className="text-white" />
          <CloseButton
            goBack={false}
            onClick={() => setOpenBottomSheet(false)}
          />
        </div>

        <div className="grid grid-cols-2 gap-2">
          {views.map((view: any) => {
            let isEnable = false;
            if (simulationData != null) {
              isEnable =
                simulationData!.povCharacters.includes(view.id) ||
                view.id === "observationCamera";
            }

            // const isEnable
            const { img, color } = getColorAndImage(view.type);
            return isEnable ? (
              <div
                className="flex gap-2"
                key={`view-${view.name}`}
                onClick={() => onSelectCharacterPov(view, img)}
              >
                <div
                  className={`flex p-[8px] gap-2 ${
                    selectedPov === view.name ? " selected-pov" : ""
                  }`}
                >
                  <div className="circle-image">
                    {defaultView.img === img ? (
                      <div className="flex items-center justify-center button-glass-blur">
                        <Image
                          aria-hidden
                          src={img}
                          alt={view.name}
                          width={24}
                          height={24}
                        />
                      </div>
                    ) : (
                      <Image
                        aria-hidden
                        src={img}
                        alt={view.name}
                        width={48}
                        height={48}
                      />
                    )}
                  </div>

                  <div className="flex items-center">
                    <div className="flex items-center rounded-lg py-[4px] gap-2">
                      {color !== "" && (
                        <div
                          className="character-color"
                          style={{
                            backgroundColor: color,
                          }}
                        />
                      )}
                      <Text
                        text={`${t(view.type)} ${view.index ?? ""}`}
                        className="text-white"
                      />
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <></>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="rounded-lg">
        <ImageButton
          className="button-glass-blur"
          src={povIcon()}
          onClick={() => onClick()}
          alt="pov icon"
          size={povIconSize()}
        />
      </div>
      {isMd ? (
        openBottomSheet && (
          <div className="fixed right-0 bottom-[100px] bottom-sheet p-[20px] rounded-xl z-50">
            <PovView />
          </div>
        )
      ) : (
        <BottomSheet
          open={openBottomSheet}
          onClose={() => setOpenBottomSheet(false)}
          initialSnap={576}
          minHeight={120}
          maxHeight={800}
        >
          <PovView />
        </BottomSheet>
      )}
    </>
  );
};

export default PovButton;
